/**
 * "Coming from your accounts" (TMMAPP-0173 item 7; TMMAPI-0202, MM-D-154): the virtual account's bank
 * money, already planned on the bank accounts. It is the forecast's own `ALLOCATED` rows grouped by
 * tmm-api's opaque `sliceKey` (one per series slice, response-scoped), in the served order. Nothing
 * is summed or worked out: each slice shows its served name, account and its next row's amount.
 */
import type { RegisterRow } from '@/api/projections';

export interface ComingSlice {
  key: string;
  /** The series name as published to the reader (the row's `payee`). */
  name: string | null;
  /** The source account, served to full readers only (MM-D-130); null for a sharer. */
  accountName: string | null;
  /** Owner-only: the series, for the link to its account's Recurring tab. */
  seriesId: number | null;
  /** The next date's served amount. */
  amountMinor: number | null;
  /** Its dates, earliest first. */
  dates: string[];
}

export function comingFromAccounts(register: readonly RegisterRow[]): ComingSlice[] {
  const bySlice = new Map<string, { rows: RegisterRow[] }>();
  for (const row of register) {
    if (row.sourceKind !== 'ALLOCATED' || row.sliceKey === null) continue;
    const entry = bySlice.get(row.sliceKey);
    if (entry === undefined) bySlice.set(row.sliceKey, { rows: [row] });
    else entry.rows.push(row);
  }
  return [...bySlice].map(([key, { rows }]) => {
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const first = sorted[0]!;
    return {
      key,
      name: first.payee,
      accountName: sorted.find((row) => row.sourceAccountName !== null)?.sourceAccountName ?? null,
      seriesId: sorted.find((row) => row.seriesId !== null)?.seriesId ?? null,
      amountMinor: first.amountMinor,
      dates: [...new Set(sorted.map((row) => row.date))],
    };
  });
}
